/**
 * Canvas component for previewing a character model during creation.
 * Draws through the character preview render worker (character-preview-render-worker.ts).
 */
import type { PreviewBackdropId } from '@sorrem-client/game/character-preview/preview-backdrops';
import { createLogger, type BodyPartModifier, type WieldedItemTier } from '@sorrem/shared';
import { resolveItemLooks } from '@sorrem-client/game/item-effect-look';
import { useEffect, useRef, useState } from 'react';
import { useRenderWorkerSession } from '@sorrem-client/ui/hooks/use-render-worker-session';
import { useCharacterTurnDrag, type CharacterTurnDrag } from './use-character-turn-drag';
import styles from "./CharacterPreviewCanvas.module.scss";
import CharacterCreateRenderWorker from "./character-create-render-worker-entry?worker";
import classnames from "classnames";
import type { CharacterPreviewCommand } from '@sorrem-client/game/render-config';

const logger = createLogger('client:character-preview-canvas');

/** Identifies a set of gear, its recolors and its items' tiers, to tell whether the worker already has it. */
function gearKey(equippedItemIds: readonly string[], outfitModifiers: Partial<Record<string, BodyPartModifier>> | undefined, itemTiersKey: string): string {
    return `${equippedItemIds.join('|')}#${outfitModifiers ? JSON.stringify(outfitModifiers) : ''}#${itemTiersKey}`;
}

interface CharacterPreviewCanvasProps {
    characterId?: string;
    equippedItemIds?: string[];
    /** Cosmetic per-slot recolor (e.g. a chosen hair color) - see `BodyPartModifier`. Applied alongside `equippedItemIds`. */
    outfitModifiers?: Partial<Record<string, BodyPartModifier>>;
    /** Each wielded item's rarity and upgrade level, keyed by its id in `equippedItemIds`: drawn as its effect (#287). */
    itemTiers?: Readonly<Record<string, WieldedItemTier>>;
    className?: string;
    /** Lets the player drag the character to turn it left and right (see `useCharacterTurnDrag`). On by default. */
    isTurnEnabled?: boolean;
    /** How a change to `equippedItemIds`/`outfitModifiers` is applied. The character always loads together with its current gear, so body and gear appear at once; this only decides what a later gear change does. On: it reloads the whole character. Off (the default): it swaps the gear live, keeping the old gear on until the new gear has loaded, which suits frequent changes (e.g. character creation). */
    isEquipmentLoadedWithCharacter?: boolean;
    /** Authored environment to render behind the platform. Omitted by default, leaving the canvas transparent for callers that draw their own background. */
    backdropId?: PreviewBackdropId;
    /**
     * On by default: idle/ambient animations play and the scene renders every
     * frame. Turn off when "Character Home effects" is below All
     * - the worker renders the scene once (character + backdrop) and then
     * stops, refreshing only when the character/equipment/backdrop actually
     * changes. Turning the character by dragging repaints the frozen frame
     * as it turns.
     */
    animationsEnabled?: boolean;
    /**
     * Fires exactly once, the first time the character (and, when
     * `backdropId` is set, its environment) has fully loaded and painted -
     * or once if loading fails, so a caller gating a loading state on this
     * doesn't get stuck forever behind the error text this component renders
     * itself. Never fires again after a character/backdrop switch; it's
     * meant for an initial "scene is ready" reveal, not per-switch loading
     * states.
     */
    onReady?: () => void;
}

export function CharacterPreviewCanvas({
    characterId,
    equippedItemIds = [],
    outfitModifiers,
    itemTiers,
    className,
    isTurnEnabled = true,
    isEquipmentLoadedWithCharacter = false,
    backdropId,
    animationsEnabled = true,
    onReady,
}: CharacterPreviewCanvasProps): JSX.Element {
    const [error, setError] = useState<string | null>(null);
    const [characterLoaded, setCharacterLoaded] = useState(false);
    const [backdropReady, setBackdropReady] = useState(false);
    const hasFiredReadyRef = useRef(false);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const equippedItemIdsRef = useRef(equippedItemIds);
    equippedItemIdsRef.current = equippedItemIds;
    const outfitModifiersRef = useRef(outfitModifiers);
    outfitModifiersRef.current = outfitModifiers;
    const itemTiersKey = itemTiers ? JSON.stringify(itemTiers) : '';
    const itemTiersRef = useRef(itemTiers);
    itemTiersRef.current = itemTiers;
    // The gear last sent to the worker (with the character or live), so the live re-equip below doesn't resend it.
    const sentGearKeyRef = useRef<string | null>(null);
    // The session's callbacks are captured once, and the turn drag is set up after the session, so its pick handler is reached through a ref.
    const handlePickResultRef = useRef<CharacterTurnDrag['handlePickResult'] | null>(null);
    // Read inside callbacks captured once at session creation (see useRenderWorkerSession's doc comment).
    const animationsEnabledRef = useRef(animationsEnabled);
    animationsEnabledRef.current = animationsEnabled;
    // Stable across renders that pass an equal-but-new array/object, so it can gate effects.
    const equipmentKey = equippedItemIds.join('|');
    const outfitModifiersKey = outfitModifiers ? JSON.stringify(outfitModifiers) : '';

    // The worker renders straight into a canvas this hook creates inside the container.
    const { containerRef, sessionRef, sessionGeneration } = useRenderWorkerSession<CharacterPreviewCommand>({
        worker: CharacterCreateRenderWorker,
        cleanRenderSource: true,
        callbacks: {
            onPickResult: (requestId, target) => handlePickResultRef.current?.(requestId, target),
            onError: (message: string) => {
                logger.error('render worker error:', message);
                setError(message);
            },
            onCharacterLoaded: () => {
                setCharacterLoaded(true);
                if (animationsEnabledRef.current) {
                    sessionRef.current?.sendCommand({ type: 'set-playing', isPlaying: true });
                } else {
                    // Static mode: paint one frame of the newly-loaded character/gear, then go idle again.
                    sessionRef.current?.sendCommand({ type: 'set-playing', isPlaying: false });
                    sessionRef.current?.sendCommand({ type: 'render-frame' });
                }
            },
            onEquipmentAttached: () => {
                // Live re-equip (character creation) changed what's on screen -
                // repaint once so a static frame reflects the new gear.
                if (!animationsEnabledRef.current) {
                    sessionRef.current?.sendCommand({ type: 'render-frame' });
                }
            },
            onBackdropReady: () => {
                setBackdropReady(true);
                // The backdrop's camera and light may settle after the
                // character's own static snapshot already painted - repaint so
                // the frozen frame reflects them too.
                if (!animationsEnabledRef.current) {
                    sessionRef.current?.sendCommand({ type: 'render-frame' });
                }
            },
        },
        // Playback is requested up front (not only once loaded) so the idle animation starts the moment the character is revealed.
        onSessionCreated: (session) => {
            if (animationsEnabledRef.current) {
                session.sendCommand({ type: 'set-playing', isPlaying: true });
            } else {
                session.sendCommand({ type: 'set-continuous-rendering', enabled: false });
            }
            setError(null);
        },
    });

    // Load (or clear) the backdrop when backdropId changes. This effect and
    // the ones below that send the worker its state run again for a fresh
    // session after a lost GPU device (#344).
    useEffect(() => {
        setBackdropReady(false);
        sessionRef.current?.sendCommand({ type: 'set-preview-backdrop', backdropId: backdropId ?? null });
    }, [sessionRef, sessionGeneration, backdropId]);

    // Fires `onReady` once, the first time the character (and its backdrop,
    // if any) has loaded - or once on error, so a caller's loading state
    // doesn't hang forever behind the inline error text below.
    useEffect(() => {
        if (hasFiredReadyRef.current) return;

        const backdropSatisfied = backdropId === undefined || backdropReady;
        if (error !== null || (characterLoaded && backdropSatisfied)) {
            hasFiredReadyRef.current = true;
            onReadyRef.current?.();
        }
    }, [characterLoaded, backdropReady, backdropId, error]);

    // React to the animations preference changing at runtime (not just at mount).
    useEffect(() => {
        const session = sessionRef.current;
        if (!session) return;

        if (animationsEnabled) {
            session.sendCommand({ type: 'set-continuous-rendering', enabled: true });
            if (characterLoaded) {
                session.sendCommand({ type: 'set-playing', isPlaying: true });
            }
        } else {
            session.sendCommand({ type: 'set-playing', isPlaying: false });
            session.sendCommand({ type: 'render-frame' });
            session.sendCommand({ type: 'set-continuous-rendering', enabled: false });
        }
    }, [sessionRef, sessionGeneration, animationsEnabled, characterLoaded]);

    // Load the character, always with its current gear so the two are revealed together, when characterId changes
    // (and, when gear changes reload the character, when the gear or its modifiers change)
    const loadWithEquipmentKey = isEquipmentLoadedWithCharacter ? equipmentKey : null;
    const loadWithModifiersKey = isEquipmentLoadedWithCharacter ? outfitModifiersKey : null;
    const loadWithTiersKey = isEquipmentLoadedWithCharacter ? itemTiersKey : null;
    useEffect(() => {
        if (!sessionRef.current || !characterId) return;

        setCharacterLoaded(false);
        sentGearKeyRef.current = gearKey(equippedItemIdsRef.current, outfitModifiersRef.current, itemTiersRef.current ? JSON.stringify(itemTiersRef.current) : '');
        sessionRef.current.sendCommand({
            type: 'load-preview-character',
            characterId,
            equippedItemDefinitionIds: equippedItemIdsRef.current,
            outfitModifiers: outfitModifiersRef.current,
            itemLooks: resolveItemLooks(itemTiersRef.current),
        });
    }, [sessionRef, sessionGeneration, characterId, isEquipmentLoadedWithCharacter, loadWithEquipmentKey, loadWithModifiersKey, loadWithTiersKey]);

    // Swap gear live once the character is loaded, when it differs from what the worker already has
    useEffect(() => {
        if (isEquipmentLoadedWithCharacter || !sessionRef.current || !characterId || !characterLoaded) return;

        const key = gearKey(equippedItemIds, outfitModifiers, itemTiersKey);
        if (key === sentGearKeyRef.current) return;
        sentGearKeyRef.current = key;

        sessionRef.current.sendCommand({
            type: 'set-equipment',
            entityKey: 'preview',
            equippedItemDefinitionIds: equippedItemIds,
            outfitModifiers,
            itemLooks: resolveItemLooks(itemTiersRef.current),
        });
    }, [sessionRef, characterId, characterLoaded, equippedItemIds, outfitModifiers, itemTiersKey, isEquipmentLoadedWithCharacter]);

    const turnDrag = useCharacterTurnDrag(containerRef, sessionRef, isTurnEnabled, animationsEnabledRef);
    handlePickResultRef.current = turnDrag.handlePickResult;

    return (
        <div
            ref={containerRef}
            className={classnames(className, styles.canvasContainer, turnDrag.isTurning && styles.turning)}
        >
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}
        </div>
    );
}
